
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCommunity } from '@/contexts/CommunityContext';
import { useBusinessParameters, useDiscountSettings } from './useParameters';
import { getDiscountSelectFields } from '@/utils/discountQueryUtils';

export type Service = {
  id: string;
  name: string;
  title?: string;
  business_id?: string;
  description: string | null;
  user_id: string;
  imageUrl?: string;
  rating?: number;
  reviewCount?: number;
  price?: number | string;
  location?: string;
  distance?: string;
  contact?: string;
  website?: string | null;
  featured?: boolean;
  discount?: {
    id: string;
    amount: number;
    isPercentage: boolean;
    appliesToAll: boolean;
    type?: 'Paid' | 'Free';
    description?: string | null;
    coupon_code?: string | null;
  } | null;
  is_active?: boolean;
  is_listed?: boolean;
  company_banner_url?: string | null;
  company_logo?: string | null;
  business_image_1_url?: string | null;
  business_image_2_url?: string | null;
  business_image_3_url?: string | null;
  companyLogo?: string | null;
  created_at: string;
  updated_at: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: {
    line1?: string | null;
    line2?: string | null;
    city?: string | null;
    county?: string | null;
    postalCode?: string | null;
    country?: string | null;
  };
  google_maps_url?: string | null;
  facebook_url?: string | null;
  instagram_url?: string | null;
  tiktok_url?: string | null;
  isfeatured?: boolean;
  images?: { id: string; url: string }[];
  city?: string;
};

export const useService = (slugOrId?: string) => {
  const { currentCommunity } = useCommunity();
  const { defaultBannerUrl } = useBusinessParameters();
  const { shouldDisplayDiscountCode } = useDiscountSettings();

  return useQuery({
    queryKey: ['service', slugOrId, currentCommunity.id, shouldDisplayDiscountCode],
    queryFn: async () => {
      if (!slugOrId) {
        throw new Error('Service slug or ID is required');
      }
      
      // Check if it's a UUID (ID) or slug
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(slugOrId);
      
      const query = supabase
        .from('businesses')
        .select('*')
        .eq('community_id', currentCommunity.id);
        
      if (isUuid) {
        query.eq('id', slugOrId);
      } else {
        query.eq('slug', slugOrId);
      }
      
      const { data: serviceData, error } = await query.maybeSingle();

      if (error) {
        throw new Error(`Error fetching service: ${error.message}`);
      }
        
      if (serviceData) {
        // Fetch reviews for this business to calculate average rating
        const { data: reviews, error: reviewsError } = await supabase
          .from('reviews')
          .select('rating')
          .eq('business_id', serviceData.id)
          .eq('status', 'Published');
          
        if (reviewsError) {
          console.error('Error fetching reviews:', reviewsError);
        }
        
        // Calculate average rating and review count
        const publishedReviews = reviews || [];
        const reviewCount = publishedReviews.length;
        const averageRating = reviewCount > 0
          ? publishedReviews.reduce((sum, review) => sum + review.rating, 0) / reviewCount
          : 0;
          
        const selectFields = getDiscountSelectFields(shouldDisplayDiscountCode);
        
        const { data: discountData, error: discountError } = await supabase
          .from('business_discounts')
          .select(selectFields)
          .eq('business_id', serviceData.id)
          .eq('is_active', true)
          .maybeSingle();
          
        if (discountError) {
          console.error('Error fetching discount:', discountError);
        }
        
        const images: { id: string; url: string }[] = [];
        
        // Use the company banner URL or fall back to the default
        const bannerUrl = serviceData.company_banner_url || defaultBannerUrl;
        
        if (bannerUrl) {
          images.push({ id: 'banner', url: bannerUrl });
        }
        
        if (serviceData.business_image_1_url) {
          images.push({ id: 'image1', url: serviceData.business_image_1_url });
        }
        if (serviceData.business_image_2_url) {
          images.push({ id: 'image2', url: serviceData.business_image_2_url });
        }
        if (serviceData.business_image_3_url) {
          images.push({ id: 'image3', url: serviceData.business_image_3_url });
        }
        
        return {
          ...serviceData,
          title: serviceData.name,
          imageUrl: bannerUrl,
          rating: averageRating,
          reviewCount,
          price: 'Contact for pricing',
          location: serviceData.city || 'Unknown location',
          companyLogo: serviceData.company_logo,
          contactEmail: serviceData.contact_email,
          contactPhone: serviceData.contact_phone,
          website: serviceData.website,
          address: {
            line1: serviceData.address_line1,
            line2: serviceData.address_line2,
            city: serviceData.city,
            county: serviceData.county,
            postalCode: serviceData.postal_code,
            country: serviceData.country
          },
          googleMapsUrl: serviceData.google_maps_url,
          socialLinks: {
            instagram: serviceData.instagram_url,
            facebook: serviceData.facebook_url,
            tiktok: serviceData.tiktok_url
          },
          featured: serviceData.isfeatured,
          discount: discountData ? {
            id: discountData.id,
            amount: discountData.discount_amount,
            isPercentage: discountData.is_percentage,
            appliesToAll: discountData.applies_to_all,
            type: discountData.type as 'Paid' | 'Free',
            description: discountData.description,
            coupon_code: discountData.coupon_code
          } : null,
          images: images.length > 0 ? images : undefined
        };
      }
      
      throw new Error(`Service with ${isUuid ? 'ID' : 'slug'} ${slugOrId} not found`);
    },
    enabled: !!slugOrId && !!currentCommunity.id,
  });
};
